import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Briefcase, ExternalLink, Check, X, Save } from "lucide-react";
import {
  getMyJobMatches,
  getMyJobProfile,
  saveMyJobProfile,
  updateMyJobMatch,
} from "@/lib/api/job-matches.functions";
import { FIELD_ALIASES } from "@/lib/job-matching";

export const Route = createFileRoute("/_authenticated/portal/job-matches")({
  component: JobMatchesPage,
  head: () => ({
    meta: [
      { title: "My job matches | NDLCS portal" },
      {
        name: "description",
        content:
          "Vacancies matched to your skills and preferred fields, delivered straight to your NDLCS portal.",
      },
    ],
  }),
});

const FIELDS = Object.keys(FIELD_ALIASES);

function JobMatchesPage() {
  const loadProfile = useServerFn(getMyJobProfile);
  const loadMatches = useServerFn(getMyJobMatches);
  const save = useServerFn(saveMyJobProfile);
  const update = useServerFn(updateMyJobMatch);
  const qc = useQueryClient();

  const profileQ = useQuery({ queryKey: ["my-job-profile"], queryFn: () => loadProfile() });
  const matchesQ = useQuery({ queryKey: ["my-job-matches"], queryFn: () => loadMatches() });

  const [skills, setSkills] = useState("");
  const [locations, setLocations] = useState("");
  const [sectors, setSectors] = useState<string[]>([]);
  const [alerts, setAlerts] = useState(true);

  useEffect(() => {
    const p: any = profileQ.data;
    if (!p) return;
    setSkills((p.skills ?? []).join(", "));
    setLocations((p.preferred_locations ?? []).join(", "));
    setSectors(p.sectors ?? []);
    setAlerts(p.alerts_enabled !== false);
  }, [profileQ.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      save({
        data: {
          skills: skills.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 40),
          preferred_locations: locations.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 40),
          sectors,
          alerts_enabled: alerts,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-job-profile"] }),
  });

  const actMut = useMutation({
    mutationFn: (v: { id: string; action: "read" | "dismiss" }) => update({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-job-matches"] }),
  });

  const matches = (matchesQ.data ?? []) as any[];
  const unread = matches.filter((m) => !m.read_at).length;

  return (
    <div className="space-y-8">
      <header>
        <h2 className="flex items-center gap-2 font-display text-2xl text-navy">
          <Briefcase className="h-5 w-5" /> My job matches
        </h2>
        <p className="mt-1 text-sm text-foreground/70">
          Tell us your skills and fields. When we scan or publish a vacancy that fits (for
          example an IT role for an IT skill set), it lands here and we email you.
        </p>
      </header>

      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h3 className="font-medium text-navy">My skills profile</h3>
        <label className="block text-sm">
          <span className="mb-1 block text-foreground/80">
            Skills (comma separated, e.g. SQL, network support, customer service)
          </span>
          <input
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            placeholder="e.g. Python, helpdesk, Microsoft 365"
          />
        </label>
        <div className="text-sm">
          <span className="mb-1 block text-foreground/80">Fields I want jobs in</span>
          <div className="flex flex-wrap gap-2">
            {FIELDS.map((f) => {
              const on = sectors.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() =>
                    setSectors((prev) => (on ? prev.filter((x) => x !== f) : [...prev, f]))
                  }
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    on
                      ? "border-navy bg-navy text-navy-foreground"
                      : "border-input hover:border-navy"
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>
        <label className="block text-sm">
          <span className="mb-1 block text-foreground/80">
            Preferred locations (comma separated)
          </span>
          <input
            value={locations}
            onChange={(e) => setLocations(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            placeholder="e.g. Benoni, Gauteng, Remote"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={alerts} onChange={(e) => setAlerts(e.target.checked)} />
          Email me when a matching vacancy is posted
        </label>
        <button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {saveMut.isPending ? "Saving…" : "Save my profile"}
        </button>
        {saveMut.isSuccess && (
          <p className="text-xs text-emerald-700">Saved — new matching vacancies will appear below.</p>
        )}
        {saveMut.error && (
          <p className="text-xs text-red-600">{(saveMut.error as Error).message}</p>
        )}
      </section>

      <section>
        <h3 className="mb-3 font-medium text-navy">
          Matched vacancies ({matches.length}
          {unread ? ` • ${unread} new` : ""})
        </h3>
        {matchesQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No matches yet. Save your skills profile above and we'll notify you as soon as a
            suitable vacancy is posted.
          </p>
        ) : (
          <ul className="space-y-2">
            {matches.map((m) => {
              const v = m.vacancies ?? {};
              return (
                <li
                  key={m.id}
                  className={`rounded-md border p-4 ${
                    m.read_at ? "border-border bg-card" : "border-navy/40 bg-navy/5"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-navy">{v.title}</p>
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-800">
                          {m.score}% match
                        </span>
                        {!m.read_at && (
                          <span className="rounded bg-navy px-1.5 py-0.5 text-[10px] uppercase text-navy-foreground">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80">
                        {v.employer}
                        {v.location ? ` • ${v.location}` : ""}
                        {v.employment_type ? ` • ${v.employment_type}` : ""}
                      </p>
                      {m.reason && (
                        <p className="mt-1 text-xs text-muted-foreground">{m.reason}</p>
                      )}
                      {v.closing_date && (
                        <p className="text-xs text-muted-foreground">Closes {v.closing_date}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <a
                        href={v.application_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => !m.read_at && actMut.mutate({ id: m.id, action: "read" })}
                        className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-xs font-medium text-navy-foreground hover:bg-navy/90"
                      >
                        Apply <ExternalLink className="h-3 w-3" />
                      </a>
                      {!m.read_at && (
                        <button
                          type="button"
                          onClick={() => actMut.mutate({ id: m.id, action: "read" })}
                          className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:border-navy"
                        >
                          <Check className="h-3 w-3" /> Mark read
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => actMut.mutate({ id: m.id, action: "dismiss" })}
                        className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:border-navy"
                      >
                        <X className="h-3 w-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
